import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import SectionLoadingView from 'components/MeshList/SectionLoadingView';
import useForceUpdate from 'components/hooks/useForceUpdate';
import usePrevious from 'components/hooks/usePrevious';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { StyledDrawer } from 'styles/admin';
import { Divider1, VerticallyAlignedFlexContainer } from 'styles/common';

import { getQueryParams } from 'constants/helper';
import { KrSvgIcon } from 'constants/icons';
import { sendAddedLevelupCommentEvent, sendClickedLevelUpSidePanelEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { Typography6 } from 'uiToolkit/Typography';

import LevelUpSidePanelActionHeader from './Header';
import ClickToScrollNav from './components/ClickToScrollNav/ClickToScrollNav';
import MeshRecommends from './components/MeshRecommends/MeshRecommends';
import useLevelUpSidePanelContext from './context';
import { getDetailsFromCompetencySidePanelRoute } from './helpers';
import ActionItemsOrInitiatives from './sections/ActionItemsOrInitiatives';
import Details from './sections/Details';
import PraiseAdviceSection from './sections/PraiseAdvice/PraiseAdviceSection';
import Progress from './sections/Progress';
import CommentSection from './sections/comments';
import CreateCompetencyDiscussion from './sections/comments/CreateCompetencyDiscussion';

const Drawer = styled(StyledDrawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      padding: 0;
    }
  }
`;

const ContentContainer = styled.div`
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
`;

const SectionList = [
  {
    id: 'progress',
    header: () => 'Progress',
    component: ({ forceUpdate, source }) => (
      <div>
        <MeshRecommends forceUpdate={forceUpdate} />
        <Progress source={source} />
        <Divider1 topGutter={2} bottomGutter={2} />
      </div>
    ),
    rightGutter: 1,
    accessibleByPeer: true,
    scrollId: 'PROGRESS',
  },
  {
    id: 'details',
    header: () => 'Details',
    component: ({ source }) => (
      <div>
        <Details source={source} />
        <Divider1 topGutter={2} bottomGutter={2} />
      </div>
    ),
    rightGutter: 1,
    accessibleByPeer: true,
    scrollId: 'DETAILS',
  },
  {
    id: 'praise',
    header: () => 'Praise/Feedback',
    component: ({ employeeId, tagId, accessible, competencyId }) => (
      <PraiseAdviceSection accessible={accessible} tagId={tagId} employeeId={employeeId} competencyId={competencyId} />
    ),
    rightGutter: 1,
    accessibleByPeer: true,
    scrollId: 'FEEDBACK',
  },
  {
    id: 'actionItems',
    header: ({ isOneOnOne }) => (isOneOnOne ? 'Action Items' : 'Initiatives'),
    component: ({ filterEmpIds, checkInInstanceId, checkInParentId, levelUpCompetencyId, competencyId, levelUpId }) => (
      <ActionItemsOrInitiatives
        checkInInstanceId={checkInInstanceId}
        competencyId={competencyId}
        levelUpId={levelUpId}
        filterEmpIds={filterEmpIds}
        checkInParentId={checkInParentId}
        levelUpCompetencyId={levelUpCompetencyId}
      />
    ),
    rightGutter: 1,
    accessibleByPeer: false,
    scrollId: 'ACTION_ITEM',
  },
  {
    id: 'comments',
    header: () => 'Comments',
    component: ({ comments, setComments }) => <CommentSection comments={comments} setComments={setComments} />,
    rightGutter: 1,
    accessibleByPeer: false,
    scrollId: 'COMMENTS',
  },
];

const StyledClickToScrollNavBar = styled(ClickToScrollNav)`
  height: 100%;
  overflow-y: auto;
  overflow-x: visible;
  > div:first-child {
    right: 0;
  }
  > div:nth-child(2) {
    padding-right: 0;
  }
`;

const StyledCreateCompetencyDiscussion = styled(CreateCompetencyDiscussion)`
  padding: 1.5rem;
  background-color: ${(props) => props.theme.palette.background.blue.light.v1};
`;

const LevelUpDrawer = ({ source = '' }) => {
  const history = useHistory();
  const { location } = history;
  const queryString = location.search;
  const previousQueryString = usePrevious(queryString);
  const currentPath = location.pathname;
  const isOpenRef = useRef(false);

  const {
    empId: employeeId,
    competencyId,
    levelUpCompetencyId,
    levelUpId,
    scrollTo,
    checkInInstanceId,
    // checkInParentId,
    isOneOnOne,
  } = getQueryParams(queryString);

  const {
    state: {
      reporteeId,
      isLoading,
      isError,
      levelUpCompetencyDetails,
      filterEmpIds,
      // checkInInstanceId,
      // checkInParentId,
      showActionItems,
    },
    closeLevelUpSidePanel,
    openLevelUpSidePanel,
  } = useLevelUpSidePanelContext();

  useEffect(() => {
    const {
      isCompetencySidePanelRoute,
      employeeId,
      competencyId,
      levelUpCompetencyId,
      levelUpId,
      scrollToId,
      checkInInstanceId,
      // checkInParentId
    } = getDetailsFromCompetencySidePanelRoute({ queryString });

    if (queryString === previousQueryString) return;

    setIsOpen(isCompetencySidePanelRoute);
    // isCompetencySidePanelRoute &&
    //   isCompetencySidePanelRoute !== isOpenRef.current &&
    //   sendOpenedLevelUpSidePanelEvent({ eventProperties: { source } });
    isOpenRef.current = isCompetencySidePanelRoute;

    if (isOpen) return;
    openLevelUpSidePanel({
      reporteeId: employeeId,
      competencyId,
      levelUpCompetencyId,
      levelUpId,
      scrollToId,
      checkInInstanceId,
      // checkInParentId
    });
  }, [queryString, previousQueryString, currentPath]);

  const [isOpen, setIsOpen] = useState(false);

  const [forceUpdateValue, forceUpdate] = useForceUpdate();

  const [comments, setComments] = useState([]);

  const handleAddNewComment = useCallback(
    (comment) => {
      setComments((comments) => [...comments, comment]);
    },
    [setComments]
  );

  const handleCloseLevelUPSidePanel = useCallback(() => {
    setComments([]);
    closeLevelUpSidePanel();
  }, [setComments, closeLevelUpSidePanel]);

  const { accessible } = levelUpCompetencyDetails ?? {};

  const accessibleSectionsArray = useMemo(() => {
    const sections = SectionList;
    if (accessible) return sections;

    return sections.filter(({ accessibleByPeer }) => accessibleByPeer);
  }, [accessible]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCloseLevelUPSidePanel}>
      <Container>
        <LevelUpSidePanelActionHeader
          closePanel={handleCloseLevelUPSidePanel}
          backButtonHandler={handleCloseLevelUPSidePanel}
          leftChild={
            <VerticallyAlignedFlexContainer>
              <KrSvgIcon style={{ marginRight: '1rem' }} color={theme.palette.icons.blue} size="fixed16px" />
              <Typography6 weight={600}> {levelUpCompetencyDetails?.competencyName}</Typography6>
            </VerticallyAlignedFlexContainer>
          }
        />

        {isLoading && (
          <div>
            <SectionLoadingView />
          </div>
        )}
        {isError && null}
        {!isLoading && !isError && (
          <>
            <div style={{ flex: '1 1 auto', overflowY: 'hidden' }}>
              <ContentContainer>
                <StyledClickToScrollNavBar
                  sectionList={accessibleSectionsArray.map(({ component, header, id, rightGutter, scrollId }) => ({
                    id,
                    header,
                    scrollId,
                    component: () =>
                      component({
                        employeeId,
                        tagId: competencyId ?? levelUpCompetencyDetails?.competencyId,
                        filterEmpIds,
                        checkInInstanceId,
                        // checkInParentId,
                        levelUpCompetencyId,
                        competencyId: competencyId ?? levelUpCompetencyDetails?.competencyId,
                        levelUpId: levelUpId ?? levelUpCompetencyDetails?.levelUpId,
                        comments,
                        setComments,
                        forceUpdate,
                        source,
                        accessible,
                        competencyName: levelUpCompetencyDetails?.competencyName,
                      }),
                    rightGutter,
                  }))}
                  scrollToId={scrollTo}
                  forceUpdateValue={forceUpdateValue}
                  isOneOnOne={isOneOnOne}
                  onNavigateToSection={(sectionId) => {
                    sendClickedLevelUpSidePanelEvent({
                      eventProperties: {
                        source,
                        sectionName: sectionId,
                      },
                    });
                  }}
                />
              </ContentContainer>
            </div>
            {accessible && (
              <div style={{ flex: '0 0 auto' }}>
                <StyledCreateCompetencyDiscussion
                  loading={false}
                  addComment={handleAddNewComment}
                  fullWidth={false}
                  onAddComment={() => {
                    sendAddedLevelupCommentEvent({
                      eventProperties: {
                        source,
                      },
                    });
                  }}
                  isDisabled={false}
                  reporteeId={reporteeId}
                  competencyId={competencyId ?? levelUpCompetencyDetails?.competencyId}
                  levelUpId={levelUpId ?? levelUpCompetencyDetails?.levelUpId}
                  subCompetencyId={levelUpCompetencyDetails.subCompetencyId}
                />
              </div>
            )}
          </>
        )}
      </Container>
    </Drawer>
  );
};

export default LevelUpDrawer;
